import { useCallback, useEffect, useMemo, useState } from 'react';
import useGetProductForStores from '../../hooks/use-get-product-for-stores';
import useGetStoreDetails from '../../hooks/use-get-store-details';
import { motion } from 'framer-motion';
import { useSelector } from 'react-redux';
import { LuIndianRupee } from 'react-icons/lu';
import StarRatingDisplay from '../../Components/StarRatingDisplay';
import { BsBookmarkHeart, BsBookmarkHeartFill } from 'react-icons/bs';
import API from '../../Common/API';
import { useHistory } from 'react-router-dom';

const StoreFront = ({ match }) => {
    const lang = useSelector((state) => state.theme.language);
    const [productList, setProductList] = useState([]);
    const history = useHistory();
    const userDetails = useSelector((state) => state.auth.userDetails);

    const { data: storeDetails, isLoading } = useGetStoreDetails(match.params.storeId);

    const { data: products, isLoading: isProductLoading } = useGetProductForStores(
        match.params.storeId
    );
    useEffect(() => {
        setProductList(products);
    }, [products]);

    const getComingSoon = useCallback(() => {
        return (
            !isProductLoading &&
            productList
                .filter((product) =>
                    product.stocks.some((stock) => stock.stockMode === 'comingSoon')
                )
                .slice(0, 10)
        );
    }, [productList]);

    const getAvailableProducts = useCallback(() => {
        return (
            !isProductLoading &&
            productList
                .filter((product) =>
                    product.stocks.some(
                        (stock) =>
                            stock.stockMode !== 'comingSoon' && stock.stockMode !== 'unavailable'
                    )
                )
                .slice(0, 10)
        );
    }, [productList]);

    const getProductDisplayName = (productDetails) => {
        if (lang === 'en') {
            const { productName, brandName, modelName } = productDetails || {};
            return [productName, brandName, modelName].filter(Boolean).join(' / ');
        } else if (lang === 'ta') {
            const { tProductName, tBrandName, tModelName } = productDetails || {};
            return [tProductName, tBrandName, tModelName].filter(Boolean).join(' / ');
        } else {
            const { productName, brandName, modelName } = productDetails || {};
            return [productName, brandName, modelName].filter(Boolean).join(' / ');
        }
    };

    const viewProduct = (value) => {
        history.push(`/product/${value._id}/view`);
    };

    const wishList = (value) => {
        let apiValues = {
            productId: value._id,
            addedBy: userDetails.subject._id,
        };
        API.post('/products/wishList', apiValues).then((wishRes) => {
            if (wishRes.status === 200) {
                setProductList(
                    productList.map((c) => {
                        if (c._id === wishRes.data.data.productId) {
                            c.wishList = [wishRes.data.data];
                        }
                        return c;
                    })
                );
            }
        });
    };

    return (
        <motion.div className="flex flex-col min-h-screen w-full relative">
            {isLoading ? (
                'Loading please wait...'
            ) : (
                <motion.div className="flex flex-row w-full justify-center text-center h-20 drop-shadow-lg bg-primeColor/20 items-center">
                    <h1 className="font-bold text-2xl">
                        {storeDetails.accountName.toString().toUpperCase()}
                    </h1>
                </motion.div>
            )}
            {getComingSoon() && getComingSoon().length > 0 && (
                <motion.div className="grid grid-cols-1 py-2">
                    <p className="text-xl font-bold px-20 text-[red]">Coming Soon Products</p>
                    <motion.div className="grid grid-cols-1 my-2 md:px-10 sm:grid-cols-1 gap-5 md:gap-10 md:grid-cols-1 xl:grid-cols-4 lg:grid-cols-4">
                        {getComingSoon() &&
                            getComingSoon()?.map((pro, i) => {
                                return (
                                    <motion.div
                                        key={i}
                                        className="relative min-h-80 md:w-72 flex flex-col bg-white cursor-pointer drop-shadow-lg"
                                    >
                                        <motion.div
                                            className="absolute top-5 right-5 text-xl"
                                            onClick={() => wishList(pro)}
                                        >
                                            {pro?.wishList?.length > 0 &&
                                            pro?.wishList[0]?.activeStat === 'A' ? (
                                                <BsBookmarkHeartFill />
                                            ) : (
                                                <BsBookmarkHeart />
                                            )}
                                        </motion.div>
                                        <motion.div
                                            className="px-2 py-2 h-60 flex flex-row justify-center"
                                            onClick={() => viewProduct(pro)}
                                        >
                                            <img
                                                src={pro?.images[0]?.imageUrl}
                                                className="object-contain"
                                                alt="preview"
                                            />
                                        </motion.div>
                                        <motion.div
                                            className="px-2 my-1 flex-row justify-between flex items-center font-bold text-xl"
                                            onClick={() => viewProduct(pro)}
                                        >
                                            <motion.div className="flex flex-row items-center">
                                                <LuIndianRupee />
                                                {pro.offerPercentage === 0
                                                    ? pro.price
                                                    : pro.price * (pro.offerPercentage / 100)}
                                            </motion.div>
                                            <StarRatingDisplay
                                                reviews={pro.reviews}
                                                homePage={true}
                                            />
                                        </motion.div>
                                        <motion.div
                                            className="px-2 my-1 flex flex-row text-black"
                                            onClick={() => viewProduct(pro)}
                                        >
                                            {getProductDisplayName(pro)}
                                        </motion.div>
                                    </motion.div>
                                );
                            })}
                    </motion.div>
                </motion.div>
            )}
            {getAvailableProducts() && (
                <motion.div className="grid grid-cols-1 py-2">
                    <p className="text-xl font-bold px-20 text-primeColor">Available Products</p>
                    <motion.div className="grid grid-cols-1 my-2 md:px-10 sm:grid-cols-1 gap-5 md:gap-10 md:grid-cols-1 xl:grid-cols-4 lg:grid-cols-4">
                        {getAvailableProducts() &&
                            getAvailableProducts()?.map((pro, i) => {
                                return (
                                    <motion.div
                                        key={i}
                                        className="relative min-h-80 md:w-72 flex flex-col bg-white cursor-pointer drop-shadow-lg"
                                    >
                                        <motion.div
                                            className="absolute top-5 right-5 text-xl"
                                            onClick={() => wishList(pro)}
                                        >
                                            {pro?.wishList?.length > 0 &&
                                            pro?.wishList[0]?.activeStat === 'A' ? (
                                                <BsBookmarkHeartFill />
                                            ) : (
                                                <BsBookmarkHeart />
                                            )}
                                        </motion.div>
                                        <motion.div
                                            className="px-2 py-2 h-60 flex flex-row justify-center"
                                            onClick={() => viewProduct(pro)}
                                        >
                                            <img
                                                src={pro?.images[0]?.imageUrl}
                                                className="object-contain"
                                                alt="preview"
                                            />
                                        </motion.div>
                                        <motion.div
                                            className="px-2 my-1 flex-row justify-between flex items-center font-bold text-xl"
                                            onClick={() => viewProduct(pro)}
                                        >
                                            <motion.div className="flex flex-row items-center">
                                                <LuIndianRupee />
                                                {pro.offerPercentage === 0
                                                    ? pro.price
                                                    : pro.price * (pro.offerPercentage / 100)}
                                            </motion.div>
                                            <StarRatingDisplay
                                                reviews={pro.reviews}
                                                homePage={true}
                                            />
                                        </motion.div>
                                        <motion.div
                                            className="px-2 my-1 flex flex-row text-black"
                                            onClick={() => viewProduct(pro)}
                                        >
                                            {getProductDisplayName(pro)}
                                        </motion.div>
                                    </motion.div>
                                );
                            })}
                    </motion.div>
                </motion.div>
            )}
        </motion.div>
    );
};

export default StoreFront;
